/**
 * Os atos de ENTRADA da integração DV360, puros (D-191, emendada pela D-192; §3.1, §5.3 e §5.6 do plano do pacote
 * Campanhas + DV360; P-33, P-34, P-57, P-58; DS-667, DS-668, DS-684):
 *
 * - **Vincular Campaigns** a uma campanha: as que a pessoa escolheu na lista pesquisável e confirmou, por ID numérico,
 *   até 20. A parte comercial do pedido não entra no ato (D-193 (7)). O Advertiser não é escolhido, informado nem
 *   autorizado: é a identidade técnica da Campaign, que o servidor resolve pelo catálogo que ele mesmo leu e grava
 *   no vínculo (DS-684 (e) e (g)). A requisição nunca chama o Google: o vínculo nasce aguardando conferência. A
 *   unicidade global (a Campaign já vinculada a outra campanha) é do banco e da API, que a conhecem. Nunca na
 *   entidade do Sandbox.
 * - **Desvincular**, o único ato de saída: motivo de 10 a 500 caracteres, em qualquer status, com a integração
 *   ligada ou não.
 * - **A capacidade de vincular** que a ficha e a Nova campanha leem (P-58), com o motivo quando não pode.
 * - **A leitura de um Advertiser** do Google (`conferirAdvertiserLidoDv360`), que o catálogo aplica a cada um.
 *
 * Nada aqui é inferido pelo nome do Advertiser, da Campaign ou da empresa (D-192): nenhum vínculo nasce sem a
 * confirmação explícita.
 */
import { motivoValido } from '../campanha/fonte-do-gasto.js'
import { permiteNoStatus } from '../campanha/status.js'
import { LIMITES_DV360 } from './limites.js'

/** O maior inteiro de 64 bits com sinal: o teto de um ID do Google (DS-661). */
const INT64_MAXIMO = 9223372036854775807n

/**
 * O ID de um recurso do Google (Advertiser, Campaign): só dígitos, sem zero à esquerda, maior que zero e cabendo em
 * 64 bits. É texto do começo ao fim (passa de 2^53): nunca vira `number`. Espaço, sinal, ponto, letra e o nome do
 * recurso são recusados — o nome nunca é aceito como chave (§3.1).
 */
export function ehIdDoGoogleDv360(texto: string): boolean {
  return /^[1-9]\d{0,18}$/.test(texto) && BigInt(texto) <= INT64_MAXIMO
}

// ------------------------------------------------------------------------------------------------ a leitura de um Advertiser

/** O que o Google devolveu de um Advertiser e o vínculo grava como identidade técnica. */
export interface AdvertiserLidoDv360 {
  readonly advertiserId: string
  readonly nome: string
  readonly moeda: string
  readonly fuso: string
}

export type RecusaDoAdvertiserLidoDv360 = 'advertiser_divergente' | 'leitura_invalida'

/**
 * A leitura do Google antes de entrar no catálogo: o ID devolvido é o esperado, o nome cabe (1 a 255), a moeda é ISO
 * 4217 (três letras maiúsculas) e o fuso tem forma. A moeda NÃO precisa estar no catálogo de moedas: a Campaign de
 * Advertiser em moeda que o catálogo não tem se vincula, com aviso, e nunca é fonte do gasto (P-57, F-11; DS-667). Que
 * o fuso exista (`pg_timezone_names`) quem confere é a API e, no vínculo, a guarda do banco.
 */
export function conferirAdvertiserLidoDv360(pedido: string, lido: AdvertiserLidoDv360): RecusaDoAdvertiserLidoDv360 | null {
  if (lido.advertiserId !== pedido) return 'advertiser_divergente'
  const tamanhoDoNome = Array.from(lido.nome.trim()).length
  if (tamanhoDoNome < 1 || tamanhoDoNome > 255) return 'leitura_invalida'
  if (!/^[A-Z]{3}$/.test(lido.moeda)) return 'leitura_invalida'
  if (lido.fuso.trim() === '' || lido.fuso.length > 64) return 'leitura_invalida'
  return null
}

// ------------------------------------------------------------------------------------------------ vincular Campaigns

export interface FatosDoVinculoDv360 {
  readonly status: string
  readonly integracaoAtiva: boolean
  /** A entidade da campanha é a do Sandbox: nunca vincula (D-192; a guarda do banco repete). */
  readonly entidadeSandbox: boolean
  /** Os IDs confirmados, na ordem do corpo. */
  readonly campaignIds: readonly string[]
  /** Quantos vínculos não anulados a campanha já tem. */
  readonly vinculadasNaCampanha: number

}

export type RecusaDoVinculoDv360 =
  | 'integracao_inativa'
  | 'entidade_sandbox'
  | 'campanha_cancelada'
  | 'status_nao_vincula'
  | 'sem_campaign'
  | 'campaign_id_invalido'
  | 'campaign_repetida'
  | 'limite_de_campaigns'

export type PlanoDoVinculoDv360 =
  | { readonly tipo: 'recusado'; readonly recusa: RecusaDoVinculoDv360; readonly campo: string }
  | {
      readonly tipo: 'vincular'
      /** Os IDs, sem as bordas, na ordem pedida. */
      readonly campaignIds: readonly string[]
    }

/**
 * Vincular uma ou mais Campaigns no mesmo ato, tudo ou nada (§3.1). A ordem das recusas é a de quem precisa agir: a
 * integração, o Sandbox e a campanha, depois os IDs e o limite. A parte comercial do pedido não entra (D-193 (7)): nem
 * recusa, nem carimbo. Que cada Campaign está no catálogo do servidor, com a leitura fresca, e livre no sistema inteiro é
 * da API (DS-684 (e)); o Advertiser de cada uma vem do catálogo, nunca daqui.
 */
export function planejarVinculoDv360(fatos: FatosDoVinculoDv360): PlanoDoVinculoDv360 {
  if (!fatos.integracaoAtiva) return { tipo: 'recusado', recusa: 'integracao_inativa', campo: '' }
  if (fatos.entidadeSandbox) return { tipo: 'recusado', recusa: 'entidade_sandbox', campo: '' }
  if (fatos.status === 'Cancelada') return { tipo: 'recusado', recusa: 'campanha_cancelada', campo: 'status' }
  if (!permiteNoStatus('vincular_campaign', fatos.status)) return { tipo: 'recusado', recusa: 'status_nao_vincula', campo: 'status' }
  if (fatos.campaignIds.length === 0) return { tipo: 'recusado', recusa: 'sem_campaign', campo: 'campaignIds' }
  const vistos = new Set<string>()
  const campaignIds: string[] = []
  for (const [posicao, pedida] of fatos.campaignIds.entries()) {
    const id = pedida.trim()
    if (!ehIdDoGoogleDv360(id)) return { tipo: 'recusado', recusa: 'campaign_id_invalido', campo: `campaignIds.${String(posicao)}` }
    if (vistos.has(id)) return { tipo: 'recusado', recusa: 'campaign_repetida', campo: `campaignIds.${String(posicao)}` }
    vistos.add(id)
    campaignIds.push(id)
  }
  // Contagem, não dinheiro: em bigint, que é exato (a regra de dinheiro do domínio libera bigint).
  if (BigInt(fatos.vinculadasNaCampanha) + BigInt(campaignIds.length) > BigInt(LIMITES_DV360.campaignsPorCampanha)) {
    return { tipo: 'recusado', recusa: 'limite_de_campaigns', campo: 'campaignIds' }
  }
  return { tipo: 'vincular', campaignIds }
}

// ------------------------------------------------------------------------------------------------ desvincular

export type RecusaDoDesvinculoDv360 = 'ja_anulado' | 'motivo_obrigatorio'

/** Desvincular: em qualquer status e com a integração ligada ou não; só o motivo e o vínculo vivo (§3.1). */
export function decidirDesvinculoDv360(fatos: { readonly anulado: boolean; readonly motivo: string }):
  { readonly recusa: RecusaDoDesvinculoDv360; readonly campo: string } | null {
  if (fatos.anulado) return { recusa: 'ja_anulado', campo: '' }
  if (!motivoValido(fatos.motivo)) return { recusa: 'motivo_obrigatorio', campo: 'motivo' }
  return null
}

// ------------------------------------------------------------------------------------------------ a capacidade (P-58)

/** Por que quem lê não pode vincular: o catálogo único, que o contrato da ficha e o do bloco DV360 repetem. */
export const MOTIVOS_SEM_VINCULO_DV360 = ['integracao_inativa', 'sandbox', 'sem_permissao', 'status'] as const
export type MotivoSemVinculoDv360 = (typeof MOTIVOS_SEM_VINCULO_DV360)[number]

/**
 * Pode vincular nesta campanha? A ficha e a Nova campanha (P-58) leem o mesmo par: verdadeiro, ou o motivo de não
 * poder, nesta ordem — a integração desligada e a entidade do Sandbox valem para todos; depois a permissão (quem edita
 * os custos); depois o status (a Cancelada não vincula).
 */
export function capacidadeDeVincularDv360(fatos: {
  readonly integracaoAtiva: boolean
  readonly entidadeSandbox: boolean
  readonly podeEditarCustos: boolean
  readonly status: string
}): { readonly pode: boolean; readonly motivo: MotivoSemVinculoDv360 | null } {
  if (!fatos.integracaoAtiva) return { pode: false, motivo: 'integracao_inativa' }
  if (fatos.entidadeSandbox) return { pode: false, motivo: 'sandbox' }
  if (!fatos.podeEditarCustos) return { pode: false, motivo: 'sem_permissao' }
  if (!permiteNoStatus('vincular_campaign', fatos.status)) return { pode: false, motivo: 'status' }
  return { pode: true, motivo: null }
}

// ------------------------------------------------------------------------------------------------ a fonte do item devolvido

export type RecusaDaAdocaoDv360 = 'fonte_do_grupo_sem_vinculo' | 'fonte_do_grupo_moeda_divergente'

/**
 * O item devolvido à composição adota a fonte do grupo (P-26, F-12). Quando ela é dv360 e a campanha não tem vínculo
 * (F-10: a fonte gravada fica ao desvincular) ou tem Campaign em outra moeda, gravar a linha dv360 esbarraria no K-04
 * (0199). A leitura escolhida (E4R-M3, DS-669) é recusar o salvamento ANTES do COMMIT, com o erro nomeado: o salvamento
 * da composição nunca troca a fonte de quem fica (F-2, D-191); quem resolve é o ato Fonte do gasto, ou o vínculo.
 */
export function recusaDaAdocaoDv360(fatos: {
  readonly adocoes: readonly { readonly fonte: 'dv360' | 'manual' }[]
  readonly temVinculo: boolean
  readonly moedaDivergente: boolean
}): RecusaDaAdocaoDv360 | null {
  if (!fatos.adocoes.some((a) => a.fonte === 'dv360')) return null
  if (!fatos.temVinculo) return 'fonte_do_grupo_sem_vinculo'
  if (fatos.moedaDivergente) return 'fonte_do_grupo_moeda_divergente'
  return null
}
